import { apiFetch } from './api-client'
import { fetchDocVolltext } from './ris-api'

type JsonObject = Record<string, unknown>

interface WebMcpTool {
  name: string
  description: string
  inputSchema: JsonObject
  execute: (input: unknown) => Promise<unknown> | unknown
}

declare global {
  interface Navigator {
    modelContext?: {
      provideContext: (context: { tools: WebMcpTool[] }) => Promise<void> | void
    }
  }
}

const COURTS = ['Justiz', 'Vfgh', 'Vwgh', 'Bvwg', 'Lvwg', 'Bfg', 'Dsk', 'Dok', 'Pvak', 'Gbk', 'Uvs', 'AsylGH', 'Ubas', 'Umse', 'Bks', 'Verg', 'Ufs']

function isRecord(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

async function fetchJson(path: string, init?: RequestInit) {
  const response = await apiFetch(path, init)
  if (!response.ok) throw new Error(`Request failed: ${response.status}`)
  return response.json() as Promise<unknown>
}

export function registerWebMcpContext() {
  const modelContext = navigator.modelContext
  if (!modelContext) return

  const tools: WebMcpTool[] = [
    {
      name: 'lexfind_search_ris',
      description: 'Search Austrian RIS case law by natural-language query, court filters, and optional date range.',
      inputSchema: {
        type: 'object',
        required: ['query'],
        properties: {
          query: { type: 'string', description: 'Search query such as "§ 25 BGG Abs 8" or "Kündigung Mutterschutz".' },
          dateFrom: { type: 'string', format: 'date' },
          dateTo: { type: 'string', format: 'date' },
          caseNumber: { type: 'string', description: 'Optional RIS Geschäftszahl / case number filter.' },
          courts: { type: 'array', items: { type: 'string', enum: COURTS } },
          dskAuthority: { type: 'string', enum: ['Datenschutzbehoerde'], description: 'Restrict Dsk search to Datenschutzbehörde.' },
        },
      },
      execute: async (rawInput) => {
        const input = isRecord(rawInput) ? rawInput : {}
        return fetchJson('/api/search', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: input.query,
            dateFrom: input.dateFrom,
            dateTo: input.dateTo,
            caseNumber: input.caseNumber,
            courts: input.courts,
            dskAuthority: input.dskAuthority,
          }),
        })
      },
    },
    {
      name: 'lexfind_fetch_ris_document',
      description: 'Fetch full text for a RIS document ID returned by LexFind search.',
      inputSchema: {
        type: 'object',
        required: ['id'],
        properties: {
          id: { type: 'string', description: 'RIS document identifier.' },
        },
      },
      execute: async (rawInput) => {
        const input = isRecord(rawInput) ? rawInput : {}
        return fetchDocVolltext(String(input.id || ''))
      },
    },
    {
      name: 'lexfind_fetch_law_versions',
      description: 'Fetch structured law versions for an Austrian legal norm.',
      inputSchema: {
        type: 'object',
        required: ['gesetzesnummer', 'paragraph'],
        properties: {
          gesetzesnummer: { type: 'string', description: 'RIS law identifier.' },
          paragraph: { type: 'string', description: 'Paragraph or article reference.' },
        },
      },
      execute: async (rawInput) => {
        const input = isRecord(rawInput) ? rawInput : {}
        const params = new URLSearchParams({
          gesetzesnummer: String(input.gesetzesnummer || ''),
          paragraph: String(input.paragraph || ''),
        })

        return fetchJson(`/api/law-text?${params.toString()}`)
      },
    },
    {
      name: 'lexfind_open_agent_mode',
      description: 'Open the LexFind agent research interface in the current browser tab.',
      inputSchema: {
        type: 'object',
        properties: {},
      },
      execute: () => {
        const url = new URL(window.location.href)
        url.pathname = '/agent'
        url.search = ''
        url.hash = ''
        window.location.href = url.toString()
        return { opened: true, url: url.toString() }
      },
    },
  ]

  // Keep WebMCP registration near the Inertia client bootstrap.
  void modelContext.provideContext({ tools })
}
